/**
 * Raw Kubernetes nodes: type + name/ready/role/ip helpers + node listing.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApi, unwrap, k8sPath, arr, k8sKeys } from './_internal';

/** Raw Kubernetes Node (status is an object, not a string). */
export interface K8sNode {
  id?: string;
  name?: string;
  metadata?: { name?: string; labels?: Record<string, string>; creationTimestamp?: string };
  status?: {
    conditions?: { type: string; status: string }[];
    addresses?: { type: string; address: string }[];
  };
}

export function nodeName(n: K8sNode): string {
  return n.metadata?.name ?? n.name ?? n.id ?? '—';
}
export function nodeReady(n: K8sNode): boolean {
  return n.status?.conditions?.find((c) => c.type === 'Ready')?.status === 'True';
}
export function nodeRole(n: K8sNode): string {
  const labels = n.metadata?.labels ?? {};
  const key = Object.keys(labels).find((k) => k.startsWith('node-role.kubernetes.io/'));
  return key ? key.split('/')[1] || 'worker' : 'worker';
}
export function nodeIp(n: K8sNode): string {
  return n.status?.addresses?.find((a) => a.type === 'InternalIP')?.address ?? '';
}

/** A provisioned worker VM from the /nodes response. The backend reports its lifecycle
 *  in the top-level `status` ("active" once up); the k8s `nodes` array stays empty while
 *  machines are still joining, so readiness is read off the machine, not node conditions. */
export interface K8sNodeMachine {
  id?: string;
  name?: string;
  status?: string;
  metadata?: Record<string, unknown>;
}

/** A worker machine is "ready" once the backend marks it active/ready/running. */
export function machineReady(m: K8sNodeMachine): boolean {
  return /active|ready|running|up/i.test(m.status ?? '');
}

/** The /nodes endpoint returns the provisioned VMs (`machines`, the worker set, each with
 *  a lifecycle `status`) plus the registered k8s `nodes` (often empty until they finish
 *  joining). The overview counts readiness off `machines`. */
export interface K8sNodesResult {
  nodes: K8sNode[];
  machines: K8sNodeMachine[];
}

export function useClusterNodes(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: k8sKeys.nodes(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async (): Promise<K8sNodesResult> => {
      const data = unwrap(await serviceApi.get(k8sPath(serviceId!, `clusters/${id}/nodes`)));
      return { nodes: arr<K8sNode>(data, 'nodes'), machines: arr<K8sNodeMachine>(data, 'machines') };
    },
    // Poll while any worker machine is still coming up (or none reported yet); stop once
    // every machine is active, to avoid steady idle traffic on a healthy cluster.
    refetchInterval: (q) => {
      const machines = q.state.data?.machines ?? [];
      return machines.length === 0 || machines.some((mm) => !machineReady(mm)) ? 10_000 : false;
    },
  });
}
